
import axios from "axios";
import type { AxiosError } from "axios";
import { getApiBaseUrl } from "@/config/apiBaseUrl";
import { captureException, withScope } from "@sentry/react";
import { clearPersistedAuthState, handleUnauthorizedSession } from "./authSession";

const baseURL = getApiBaseUrl();

const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;
const MISSING_API_BASE_URL_MESSAGE =
  "Admin API URL is not configured: VITE_API_BASE_URL is missing from runtime config.";

type SentryTaggedError = AxiosError & { __sentryReported?: boolean };

type PersistedAuthState = {
  accessToken?: string;
};

// Cache for parsed auth with simple TTL to avoid repeated parsing on every request
let cachedAuth: PersistedAuthState | null = null;
let lastParseTime = 0;
const PARSE_CACHE_TTL_MS = 100; // Re-parse if storage might have changed

function parsePersistedAuth(): PersistedAuthState | null {
  const now = Date.now();
  if (cachedAuth && now - lastParseTime < PARSE_CACHE_TTL_MS) {
    return cachedAuth;
  }

  lastParseTime = now;

  if (globalThis.sessionStorage === undefined) {
    cachedAuth = null;
    return null;
  }

  const persisted = globalThis.sessionStorage.getItem("persist:root");
  if (!persisted) {
    cachedAuth = null;
    return null;
  }

  try {
    const parsed = JSON.parse(persisted) as { auth?: string };
    if (!parsed.auth) {
      cachedAuth = null;
      return null;
    }

    const auth = JSON.parse(parsed.auth) as PersistedAuthState;
    if (!auth.accessToken || typeof auth.accessToken !== "string") {
      cachedAuth = null;
      return null;
    }

    cachedAuth = auth;
    return auth;
  } catch {
    clearPersistedAuthState();
    cachedAuth = null;
    return null;
  }
}

function isAuthRequest(url?: string): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }
  try {
    const pathname = new URL(url, baseURL).pathname;
    return pathname === "/v1/auth/login/admin";
  } catch {
    return false;
  }
}

const axiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    // Without a configured API URL a relative request resolves against the admin
    // host itself, which answers 405. Fail here so the error names the cause.
    if (!baseURL && !ABSOLUTE_URL_PATTERN.test(config.url ?? "")) {
      throw new Error(MISSING_API_BASE_URL_MESSAGE);
    }

    const auth = parsePersistedAuth();
    if (auth?.accessToken) {
      config.headers.Authorization = `Bearer ${auth.accessToken}`;
    }

    if (!config.headers["Content-Type"] && !config.headers["content-type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

function captureUnexpectedApiError(error: AxiosError): void {
  if (
    error instanceof Error
    && (error.name === "AbortError" || error.name === "CanceledError")
  ) {
    return;
  }

  const status = error.response?.status;
  // Skip 4xx (expected client/auth/validation failures). Capture 5xx and network errors.
  if (status !== undefined && status < 500) {
    return;
  }

  const tagged = error as SentryTaggedError;
  if (tagged.__sentryReported) {
    return;
  }
  tagged.__sentryReported = true;

  const config = error.config;
  const rawUrl = config?.url ?? "";
  let path = rawUrl;
  try {
    path = new URL(rawUrl, "http://localhost").pathname;
  } catch {
    // ignore URL parse errors; use raw path
  }

  withScope((scope) => {
    scope.setTags({ action: "http_request", component: "api_client" });
    scope.setExtras({
      message: error.message,
      method: config?.method?.toUpperCase(),
      path,
      status,
    });
    captureException(error);
  });
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error?.response?.status === 401 && !isAuthRequest(error?.config?.url)) {
      handleUnauthorizedSession();
    } else {
      captureUnexpectedApiError(error);
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
